import NeptuneElement from '../../NeptuneElement.js';
import SideNavItem from './sidenavItem/SidenavItem.js';
import { i18nMixin } from '../../../utils/i18nMixin.js';
import { i18n } from '../../../utils/i18n.js';

class Sidenav extends i18nMixin(NeptuneElement) {

    static SIDENAV_STATES = {
        rail: 'rail',
        primaryDrawer: 'primary-drawer',
        secondaryDrawer: 'secondary-drawer'
    };

    templateMarkup = `
        <nav id="sidenav">
            <button class="font-size-md">
                <i class="fa-regular fa-chevron-double-right"></i>
                <span></span>
            </button>            
            <button class="font-size-md" hidden>
                <i class="fa-regular fa-arrow-left"></i>
                <span class="back-label"></span>
            </button>
            <menu class="top-menu"></menu>
            <hr hidden>
            <menu class="middle-menu"></menu>
            <hr hidden>
            <menu class="bottom-menu"></menu>
            <hr hidden>
            <menu class="secondary-menu" hidden></menu>           
        </nav>
    `;

    componentCssUrl = '/src/components/chrome/sidenav/sidenav.css';

    menuData = [];

    #activeParentLabel = null;

    #activeRoute = null;

    #currentParentLabel = null;
    #isMoreItemsView = false;
    #drilledFromMore = false;
    #moreItemsList = null;

    async connectedCallback() {
        await super.connectedCallback();
        const culture = this.getAttribute('culture');
        if (culture) { i18n.setLocale(culture); }
        this.#applyTranslations();
        requestAnimationFrame(() => this.#renderPrimaryMenu());
        const state = this.getAttribute('state') || Sidenav.SIDENAV_STATES.rail;
        const nav = this.shadowRoot.querySelector('nav');
        if (nav) {
            nav.dataset.state = state;
        }
        this.#updateNavItemsCollapsedState(state === Sidenav.SIDENAV_STATES.rail);
        document.dispatchEvent(new CustomEvent('sidenav-ready', {
            detail: { sidenavElement: this }
        }));
    }

    /**
     * @description Handles locale changes by applying translations and updating button labels based on the current sidenav state.
     * This method is called when the 'lang' attribute changes.
     * It ensures that the toggle and back button labels are updated to reflect the new language.
     */
    onLocaleChange() {
        this.#applyTranslations();
        this.#updateToggleButton(
            (this.getAttribute('state') || Sidenav.SIDENAV_STATES.rail) === Sidenav.SIDENAV_STATES.rail,
            this.getAttribute('state') === Sidenav.SIDENAV_STATES.secondaryDrawer
        );
    }

    /**
     * @description Applies translations to the toggle and back button labels based on the current sidenav state.
     * It retrieves the appropriate translation for the toggle button (expand/collapse) and back button.
     * This method is called during component initialization and whenever the 'lang' attribute changes.
     * It ensures that the button labels are always displayed in the correct language.
     */
    #applyTranslations() {
        const toggleLabel = this.shadowRoot?.querySelector('nav > button:first-of-type span');
        const backLabel = this.shadowRoot?.querySelector('nav > button:nth-of-type(2) span');
        if (backLabel && this.#isMoreItemsView) { 
            this.#currentParentLabel = this.translate('SIDENAV.VIEW_MORE');
            backLabel.textContent = this.translate('SIDENAV.VIEW_MORE'); }
        const state = this.getAttribute('state') || Sidenav.SIDENAV_STATES.rail;
        const viewMore = this.shadowRoot?.querySelector('.more-items-button');
        if (viewMore) {
            viewMore.setAttribute('label', this.translate('SIDENAV.VIEW_MORE'));
        }
        if (toggleLabel) {
            toggleLabel.textContent = this.translate(state === Sidenav.SIDENAV_STATES.rail ? 'SIDENAV.EXPAND' : 'SIDENAV.COLLAPSE');
        }
    }

    async disconnectedCallback() {
        await super.disconnectedCallback();

        document.dispatchEvent(new CustomEvent('sidenav-removed', {
            detail: { sidenavElement: this }
        }));
    }

    static get observedAttributes() {
        return ['state', 'culture'];
    }

    setEventListeners() {
        this.toggleButton = this.shadowRoot.querySelector('nav > button:first-of-type');
        this.backButton = this.shadowRoot.querySelector('nav > button:nth-of-type(2)');

        this.handleToggleBound = this.#handleToggleClick.bind(this);
        this.handleGoBackBound = this.#goBackToDrawer.bind(this);
        this.handleItemToggleBound = this.#handleItemToggle.bind(this);
        this.handleItemClickBound = this.#handleItemClick.bind(this);
        this.handleDrawerOpenBound = this.#handleDrawerOpen.bind(this);
        this.handleLocationChangeBound = this.#handleLocationChange.bind(this);

        this.toggleButton?.addEventListener('click', this.handleToggleBound);
        this.backButton?.addEventListener('click', this.handleGoBackBound);
        this.addEventListener('neptune-sidenav-item-toggle', this.handleItemToggleBound);
        this.addEventListener('neptune-sidenav-item-click', this.handleItemClickBound);
        document.addEventListener('neptune-drawer-open', this.handleDrawerOpenBound);

        this.patchHistoryNavigation();

        window.addEventListener('neptune-location-change', this.handleLocationChangeBound);
        window.addEventListener('popstate', this.handleLocationChangeBound);

        this.handleResizeBound = () => this.#renderPrimaryMenu();
        window.addEventListener('resize', this.handleResizeBound);
    }

    removeEventListeners() {
        this.toggleButton?.removeEventListener('click', this.handleToggleBound);
        this.backButton?.removeEventListener('click', this.handleGoBackBound);
        this.removeEventListener('neptune-sidenav-item-toggle', this.handleItemToggleBound);
        this.removeEventListener('neptune-sidenav-item-click', this.handleItemClickBound);
        document.removeEventListener('neptune-drawer-open', this.handleDrawerOpenBound);
        window.removeEventListener('neptune-location-change', this.handleLocationChangeBound);
        window.removeEventListener('popstate', this.handleLocationChangeBound);

        window.removeEventListener('resize', this.handleResizeBound);
        this.handleResizeBound = null;

        this.toggleButton = null;
        this.backButton = null;
        this.handleToggleBound = null;
        this.handleGoBackBound = null;
        this.handleItemToggleBound = null;
        this.handleItemClickBound = null;
        this.handleDrawerOpenBound = null;
        this.handleLocationChangeBound = null;
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    /**
     * Processes attribute changes and updates the component state accordingly.
     * @param {string} name - The attribute name
     * @param {string} oldValue - The previous attribute value
     * @param {string} newValue - The new attribute value
     */
    processAttribute(name, oldValue, newValue) {
        switch (name) {
            case 'culture': {
                if (newValue) { i18n.setLocale(newValue); }
                break;
            }
            case 'state': {
                if (!this.shadowRoot) { return; }

                const nav = this.shadowRoot.querySelector('nav');
                if (!nav) { return; }

                const state = newValue || Sidenav.SIDENAV_STATES.rail;
                nav.dataset.state = state;
                if (window.innerWidth <= 720 &&
                    state === Sidenav.SIDENAV_STATES.primaryDrawer) {
                    requestAnimationFrame(() => this.#renderPrimaryMenu());
                }
                document.body.setAttribute('data-sidenav-state', state);

                const isRail = state === Sidenav.SIDENAV_STATES.rail;
                const isSecond = state === Sidenav.SIDENAV_STATES.secondaryDrawer;

                this.#updateToggleButton(isRail, isSecond);
                this.#updateBackButton(isSecond);
                this.#updateMenuVisibility(isSecond);
                this.#updateNavItemsCollapsedState(isRail);
                if (!isSecond) {
                    this.shadowRoot.querySelectorAll('neptune-sidenav-item[expanded]')
                        .forEach(element => element.removeAttribute('expanded'));
                }
                break;
            }
            default: {
                break;
            }
        }
    }

    /**
     * Retrieves all sidenav items from both primary menu and slotted bottom menu.
     * @returns {HTMLElement[]}
     */
    #allItems() {
        const slot = this.shadowRoot?.querySelector('slot[name="bottom-menu"]');
        const slotted = slot?.assignedElements().flatMap(el => [...el.querySelectorAll('neptune-sidenav-item')]) ?? [];
        return [...(this.shadowRoot?.querySelectorAll('neptune-sidenav-item') ?? []), ...slotted];
    }

    /**
     * Clears the active state from all navigation items.
     */
    clearActive() {
        this.#activeRoute = null;
        this.#allItems().forEach(el => { el.removeAttribute('active'); el.setAttribute('state', 'default'); });
    }

    /**
     * Normalizes a route by removing a leading hash character if present.
     * Allows route matching to work with both hash (#/route) and path (/route) formats.
     * @param {string} route - The route path to normalize
     * @returns {string}
     */
    #normalizeRoute(route) {
        return route?.replace(/^#/, '') ?? '';
    }

    /**
     * Sets the active route and highlights the matching navigation item(s).
     * If the route matches a submenu item, the parent item is also highlighted.
     * @param {string} route - The route path to activate
     */
    setActiveRoute(route) {
        if (!route) { this.clearActive(); return; }
        this.#activeRoute = route;
        const normalized = this.#normalizeRoute(route);

        this.#allItems().forEach(navItem => {
            const match = this.#normalizeRoute(navItem.getAttribute('route')) === normalized;
            navItem.toggleAttribute('active', match);
            navItem.setAttribute('state', match ? 'active' : 'default');
        });

        let parentInMenu = null;
        if (this.menuData.topMenuItems || this.menuData.middleMenuItems || this.menuData.bottomMenuItems) {
            const allItems = [
                ...(this.menuData.topMenuItems || []),
                ...(this.menuData.middleMenuItems || []),
                ...(this.menuData.bottomMenuItems || [])
            ];
            parentInMenu = allItems.find(menuItem => {
                const subItems = menuItem.subMenu || menuItem.subMenuItems || [];
                return subItems.some(subItem => {
                    const subItemRoute = subItem.route || subItem.link || '';
                    return this.#normalizeRoute(subItemRoute) === normalized;
                });
            });
        } else {
            const flat = Array.isArray(this.menuData) ? this.menuData : [this.menuData];
            parentInMenu = flat.find(menuItem => {
                const subItems = menuItem.subMenu || menuItem.subMenuItems || [];
                return subItems.some(subItem => {
                    const subItemRoute = subItem.route || subItem.link || '';
                    return this.#normalizeRoute(subItemRoute) === normalized;
                });
            });
        }

        if (parentInMenu) {
            this.#activeParentLabel = parentInMenu.label || parentInMenu.name;
            const parentEl = this.#allItems().find(item =>
                (item.getAttribute('label') || item.getAttribute('name')) === this.#activeParentLabel
            );
            if (parentEl) {
                parentEl.setAttribute('active', '');
                parentEl.setAttribute('state', 'active');
            }
        } 
    }

    /**
     * Updates the navigation menu data and re-renders if the component is already rendered.
     * Normalizes different input shapes into a consistent structure:
     *  - [{ menu: { topMenuItems, middleMenuItems, bottomMenuItems } }]  (wrapper array)
     *  - { menu: { topMenuItems, middleMenuItems, bottomMenuItems } }    (wrapper object)
     *  - { topMenuItems, middleMenuItems, bottomMenuItems }              (direct object)
     * @param {Array|Object} data - Menu data in any supported shape
    */
    setMenuData(data) {
        const normalized = this.#normalizeMenuData(data);
        if (JSON.stringify(this.menuData) === JSON.stringify(normalized)) { return; }
        this.menuData = normalized;
        if (this.isRendered) { this.#renderPrimaryMenu(); }
    }

    /**
     * Unwraps and normalizes incoming menu data into either a segregated object
     * ({ topMenuItems, middleMenuItems, bottomMenuItems }) or a legacy flat array.
     * @param {Array|Object} data - The raw menu data passed by the consumer
     * @returns {Object|Array} Normalized menu data
     */
    #normalizeMenuData(data) {
        if (!data) { return []; }

        let candidate = Array.isArray(data) ? data[0] : data;

        if (candidate && candidate.menu) {
            candidate = candidate.menu;
        }

        if (candidate && (candidate.topMenuItems || candidate.middleMenuItems || candidate.bottomMenuItems)) {
            return JSON.parse(JSON.stringify({
                topMenuItems: candidate.topMenuItems || [],
                middleMenuItems: candidate.middleMenuItems || [],
                bottomMenuItems: candidate.bottomMenuItems || []
            }));
        }

        return Array.isArray(data) ? JSON.parse(JSON.stringify(data)) : [];
    }

    /**
     * Updates the toggle button icon and label based on the current sidenav state.
     * @param {boolean} isRail - Whether the sidenav is in rail state
     * @param {boolean} isSecond - Whether the sidenav is in secondary drawer state
     */
    #updateToggleButton(isRail, isSecond) {
        const toggleBtn = this.shadowRoot.querySelector('nav > button:first-of-type');
        if (!toggleBtn) { return; }

        const toggleIcon = toggleBtn.querySelector('i');
        const toggleLabel = toggleBtn.querySelector('span');

        if (toggleIcon) {
            toggleIcon.className = `fa-regular fa-chevron-double-${isRail ? 'right' : 'left'}`;
        }
        if (toggleLabel) {
            toggleLabel.textContent = this.translate(isRail ? 'SIDENAV.EXPAND' : 'SIDENAV.COLLAPSE');
        }
        toggleBtn.hidden = isSecond;
    }

    /**
     * Updates the back button visibility based on whether the sidenav is in secondary drawer state.
     * @param {boolean} isSecond - Whether the sidenav is in secondary drawer state
     */
    #updateBackButton(isSecond) {
        const backBtn = this.shadowRoot.querySelector('nav > button:nth-of-type(2)');
        if (!backBtn) { return; }
        backBtn.hidden = !isSecond;
    }

    /**
     * Updates the visibility of primary and secondary menus based on the current state.
     * @param {boolean} isSecond - Whether the sidenav is in secondary drawer state
     */
    #updateMenuVisibility(isSecond) {
        const topMenu = this.shadowRoot.querySelector('menu.top-menu');
        const middleMenu = this.shadowRoot.querySelector('menu.middle-menu');
        const bottomMenu = this.shadowRoot.querySelector('menu.bottom-menu');
        const secondaryMenu = this.shadowRoot.querySelector('menu.secondary-menu');
        const hr1 = this.shadowRoot.querySelector('hr:first-of-type');
        const hr2 = this.shadowRoot.querySelector('hr:nth-of-type(2)');
        const hr3 = this.shadowRoot.querySelector('hr:nth-of-type(3)');

        if (topMenu) {
            topMenu.hidden = isSecond;
        }
        if (middleMenu) {
            middleMenu.hidden = isSecond;
        }
        if (bottomMenu) {
            bottomMenu.hidden = isSecond;
        }
        if (hr1) {
            hr1.hidden = isSecond;
        }
        if (hr2) {
            hr2.hidden = isSecond;
        }

        if (secondaryMenu) {
            secondaryMenu.hidden = !isSecond;
            hr3.hidden = !isSecond;
        }
    }

    /**
     * Updates the collapsed state of all primary and slotted navigation items.
     * @param {boolean} collapsed - Whether items should be collapsed
     */
    #updateNavItemsCollapsedState(collapsed) {
        const primaryMenuItems = this.shadowRoot.querySelectorAll('menu neptune-sidenav-item');
        const bottomMenuItems = this.querySelectorAll('[slot="bottom-menu"] neptune-sidenav-item');
        [...primaryMenuItems, ...bottomMenuItems].forEach(el => {
            if (collapsed) {
                el.setAttribute('collapsed', 'true');
            } else {
                el.removeAttribute('collapsed');
            }
        });
    }

    /**
     * Handles toggle button click to switch between rail and primary-drawer states.
     */
    #handleToggleClick() {
        const current = this.getAttribute('state') || Sidenav.SIDENAV_STATES.rail;
        const next = current === Sidenav.SIDENAV_STATES.rail
            ? Sidenav.SIDENAV_STATES.primaryDrawer
            : Sidenav.SIDENAV_STATES.rail;

        this.setAttribute('state', next);
        this.#emitToggle(next);
    }

    /**
     * Handles item toggle events to expand/collapse submenus or ensure drawer state.
     * @param {CustomEvent} event - The neptune-sidenav-item-toggle event
     */
    #handleItemToggle(event) {
        const { expanded, label } = event.detail;
        if (expanded) {
            const isRail = (this.getAttribute('state') || Sidenav.SIDENAV_STATES.rail) === Sidenav.SIDENAV_STATES.rail;
            if (isRail) {
                this.shadowRoot.querySelectorAll('neptune-sidenav-item[expanded]')
                    .forEach(el => el.removeAttribute('expanded'));
                this.setAttribute('state', Sidenav.SIDENAV_STATES.primaryDrawer);
                this.#emitToggle(Sidenav.SIDENAV_STATES.primaryDrawer);
                return;
            }
            const item = this.#findMenuItem(label);
            const subMenuItems = item?.subMenu || item?.subMenuItems;
            if (subMenuItems) {
                this.#activeParentLabel = label;
                this.#currentParentLabel = label;
                if (this.#isMoreItemsView) {
                    this.#drilledFromMore = true;
                }
                this.#openSecondary(subMenuItems);
            }
        } else {
            this.#ensureDrawer();
        }
    }

    /**
     * Handles item click events to set the active route and emit navigation event.
     * @param {CustomEvent} event - The neptune-sidenav-item-click event
     */
    #handleItemClick(event) {
        this.setActiveRoute(event.detail.route);
        const current = this.getAttribute('state') || Sidenav.SIDENAV_STATES.rail;
        if (current !== Sidenav.SIDENAV_STATES.rail) {
            this.shadowRoot.querySelectorAll('neptune-sidenav-item[expanded]')
                .forEach(el => el.removeAttribute('expanded'));
            this.setAttribute('state', Sidenav.SIDENAV_STATES.rail);
            this.#emitToggle(Sidenav.SIDENAV_STATES.rail);
        }
        this.dispatchEvent(new CustomEvent('neptune-sidenav-navigate', {
            bubbles: true, composed: true,
            detail: { id: event.detail.id, route: event.detail.route, label: event.detail.label }
        }));

        window.dispatchEvent(new CustomEvent('neptune-location-change', {
            detail: { route: event.detail.route }
        }));

    }

    /**
     * Handles location change events from browser navigation or history changes.
     * Updates the active route based on the new location.
     * @param {Event} event - The neptune-location-change event or popstate event
     */
    #handleLocationChange(event) {
        const newRoute = this.#normalizeRoute(event?.detail?.route ?? window.location.pathname);
        if (this.#activeRoute && newRoute === this.#normalizeRoute(this.#activeRoute)) { return; }
        this.setActiveRoute(newRoute);
    }

    /**
     * Handles drawer open events to collapse the sidenav when another drawer opens.
     */
    #handleDrawerOpen() {
        if (this.getAttribute('state') === Sidenav.SIDENAV_STATES.rail) {
            return;
        }
        this.setAttribute('state', Sidenav.SIDENAV_STATES.rail);
        this.#emitToggle(Sidenav.SIDENAV_STATES.rail);
    }

    /**
     * Opens the secondary drawer with the provided submenu items.
     * @param {Array} subMenuItems - Array of submenu item data
     */
    #openSecondary(subMenuItems) {
        this.#isMoreItemsView = false;
        this.#populateSecondaryMenu(subMenuItems);
        this.#transitionToSecondDrawer();
    }

    /**
     * Populates the secondary menu with items and restores active state if applicable.
     * @param {Array} subMenuItems - Array of submenu item data
     */
    #populateSecondaryMenu(subMenuItems) {
        const menu = this.shadowRoot.querySelector('menu.secondary-menu');
        if (menu) {
            menu.innerHTML = '';
            subMenuItems.forEach(item => {
                const el = this.#createNavItem(item, false);
                const itemRoute = item.route || item.link || '';
                if (this.#activeRoute && this.#normalizeRoute(itemRoute) === this.#normalizeRoute(this.#activeRoute)) {
                    el.setAttribute('active', '');
                    el.setAttribute('state', 'active');
                }
                menu.appendChild(el);
            });
        }
    }

    /**
     * Transitions the sidenav to secondary drawer state and emits a toggle event.
     * Updates the back button label with the parent menu item name.
     */
    #transitionToSecondDrawer() {
        const backLabel = this.shadowRoot?.querySelector('.back-label');
        if (backLabel && this.#currentParentLabel) {
            backLabel.textContent = this.#currentParentLabel;
        }
        this.setAttribute('state', Sidenav.SIDENAV_STATES.secondaryDrawer);
        this.#emitToggle(Sidenav.SIDENAV_STATES.secondaryDrawer);
        requestAnimationFrame(() => {
            this.shadowRoot?.querySelectorAll('menu.secondary-menu neptune-sidenav-item')
            .forEach(el => el.refreshTooltip?.());
        });
    }

    /**
     * Navigates back from secondary drawer to primary drawer and clears expanded states.
     */
    #goBackToDrawer() {
        this.shadowRoot.querySelectorAll('neptune-sidenav-item[expanded]')
            .forEach(el => el.removeAttribute('expanded'));

        if (this.#drilledFromMore && this.#moreItemsList) {
            this.#drilledFromMore = false;
            this.#isMoreItemsView = true;
            this.#openSecondaryForMoreItems(this.#moreItemsList, this.translate('SIDENAV.VIEW_MORE'));
            return;
        }

        this.#isMoreItemsView = false;
        this.setAttribute('state', Sidenav.SIDENAV_STATES.primaryDrawer);
        this.#emitToggle(Sidenav.SIDENAV_STATES.primaryDrawer);
    }

    /**
     * Opens secondary drawer with additional hidden items from middle section.
     * @param {Array} items - Additional menu items to display
     * @param {string} parentLabel - Label to display in back button
     */
    #openSecondaryForMoreItems(items, parentLabel) {
        this.#currentParentLabel = parentLabel;
        const secondaryMenu = this.shadowRoot?.querySelector('menu.secondary-menu');
        if (secondaryMenu) {
            secondaryMenu.innerHTML = '';
            items.forEach(item => {
                const el = this.#createNavItem(item, false);
                const itemRoute = item.route || item.link || '';
                const subItems = item.subMenu || item.subMenuItems || [];
                const isActiveSelf = this.#activeRoute &&
                    this.#normalizeRoute(itemRoute) === this.#normalizeRoute(this.#activeRoute);
                const isActiveParent = this.#activeRoute &&
                    subItems.some(s => this.#normalizeRoute(s.route || s.link || '') === this.#normalizeRoute(this.#activeRoute));
                if (isActiveSelf || isActiveParent) {
                    el.setAttribute('active', '');
                    el.setAttribute('state', 'active');
                }
                secondaryMenu.appendChild(el);
            });
        }
        this.#transitionToSecondDrawer();
    }

    /**
     * Ensures the sidenav is in at least primary-drawer state if not already.
     */
    #ensureDrawer() {
        if (this.getAttribute('state') !== Sidenav.SIDENAV_STATES.primaryDrawer) {
            this.setAttribute('state', Sidenav.SIDENAV_STATES.primaryDrawer);
            this.#emitToggle(Sidenav.SIDENAV_STATES.primaryDrawer);
        }
    }

    /**
     * Renders top/middle/bottom menu items.
     * Middle items fill the available viewport space; anything that doesn't fit
     * collapses into a "..." overflow button.
     */
    #renderPrimaryMenu() {
        const topMenu = this.shadowRoot?.querySelector('menu.top-menu');
        const middleMenu = this.shadowRoot?.querySelector('menu.middle-menu');
        const bottomMenu = this.shadowRoot?.querySelector('menu.bottom-menu');
        const hr1 = this.shadowRoot?.querySelector('hr:first-of-type');
        const hr2 = this.shadowRoot?.querySelector('hr:nth-of-type(2)');

        if (!topMenu || !middleMenu || !bottomMenu) { return; }

        topMenu.innerHTML = '';
        middleMenu.innerHTML = '';
        bottomMenu.innerHTML = '';
        if (hr1) { hr1.hidden = true; }
        if (hr2) { hr2.hidden = true; }

        const collapsed = (this.getAttribute('state') || Sidenav.SIDENAV_STATES.rail) === Sidenav.SIDENAV_STATES.rail;

        if (this.menuData.topMenuItems || this.menuData.middleMenuItems || this.menuData.bottomMenuItems) {
            const topItems = (this.menuData.topMenuItems || []).slice(0, 2);
            const middleItems = this.menuData.middleMenuItems || [];
            const bottomItems = (this.menuData.bottomMenuItems || []).slice(0, 2);

            if ((this.menuData.topMenuItems || []).length > 2 || (this.menuData.bottomMenuItems || []).length > 2) {
                console.warn('[neptune-sidenav] topMenuItems and bottomMenuItems support a maximum of 2 items each. Additional items will be ignored.');
            }

            topItems.forEach(item => topMenu.appendChild(this.#createNavItem(item, collapsed)));

            if (bottomItems.length > 0 && (topItems.length > 0 || middleItems.length > 0)) { hr2.hidden = false; }
            bottomItems.forEach(item => bottomMenu.appendChild(this.#createNavItem(item, collapsed)));

            if (middleItems.length > 0) {
                if (topItems.length > 0) { hr1.hidden = false; }
                middleItems.forEach(item => middleMenu.appendChild(this.#createNavItem(item, collapsed)));
                this.#fitMiddleItems(middleItems, collapsed);
            }
        } else {
            const primaryMenu = this.shadowRoot?.querySelector('menu:first-of-type');
            if (primaryMenu) {
                primaryMenu.innerHTML = '';
                this.menuData.forEach(group => {
                    const items = (group.menu && !group.legacy) ? group.menu : [group];
                    items.forEach(item => primaryMenu.appendChild(this.#createNavItem(item, collapsed)));
                });
            }
        }

        if (this.#activeRoute) {
            requestAnimationFrame(() => {
                this.setActiveRoute(this.#activeRoute);
            });
        }
    }

    /**
    * Handles middle menu overflow and creates a "View More" option.
    * @param {Object[]} middleItems - Middle menu items.
    * @param {boolean} collapsed - Collapsed state.
    */
    #fitMiddleItems(middleItems, collapsed) {
        const nav = this.shadowRoot?.querySelector('nav');
        const middleMenu = this.shadowRoot?.querySelector('menu.middle-menu');
        if (!nav || !middleMenu || !middleItems.length) { return; }

        requestAnimationFrame(() => {
            const cs = getComputedStyle(nav);
            const navInner = nav.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
            if (navInner < 100) { return; }

            const firstItem = middleMenu.querySelector('neptune-sidenav-item');
            const ITEM_HEIGHT = firstItem?.offsetHeight || 44;
            const TOGGLE_HEIGHT = 56;
            const HR_HEIGHT = 17;

            const topCount = (this.menuData.topMenuItems || []).length;
            const bottomCount = (this.menuData.bottomMenuItems || []).length;
            const hrCount = (topCount > 0 ? 1 : 0) + (bottomCount > 0 ? 1 : 0);

            const reserved = TOGGLE_HEIGHT
                + (topCount * ITEM_HEIGHT)
                + (bottomCount * ITEM_HEIGHT)
                + (hrCount * HR_HEIGHT);

            const availableForMiddle = navInner - reserved;
            const maxVisibleItems = Math.max(1, Math.floor(availableForMiddle / ITEM_HEIGHT));

            if (maxVisibleItems >= middleItems.length) { return; }
            const visibleCount = Math.max(1, maxVisibleItems - 1);
            const visibleItems = middleItems.slice(0, visibleCount);
            const hiddenItems = middleItems.slice(visibleCount);

            middleMenu.innerHTML = '';
            visibleItems.forEach(item => middleMenu.appendChild(this.#createNavItem(item, collapsed)));

            const moreButton = document.createElement('neptune-sidenav-item');
            moreButton.setAttribute('label', this.translate('SIDENAV.VIEW_MORE'));
            moreButton.setAttribute('icon', 'fa-ellipsis');
            moreButton.setAttribute('route', '#');
            moreButton.classList.add('more-items-button');
            if (collapsed) {
                moreButton.setAttribute('collapsed', 'true');
            }
            moreButton.addEventListener('neptune-sidenav-item-click', (e) => {
                e.stopPropagation();
                this.#isMoreItemsView = true;
                this.#moreItemsList = hiddenItems;
                this.#openSecondaryForMoreItems(hiddenItems, this.translate('SIDENAV.VIEW_MORE'));
            });
            middleMenu.appendChild(moreButton);
        });
    }

    /**
     * Creates a neptune-sidenav-item element from menu item data.
     * Supports both Font Awesome icons and image URLs.
     * @param {Object} item - Menu item data with properties like name, route, icon, image, subMenu
     * @param {boolean} collapsed - Whether the item should be in collapsed state
     * @returns {HTMLElement}
     */
    #createNavItem(item, collapsed) {
        const el = document.createElement('neptune-sidenav-item');
        const label = item.label || item.name || '';
        el.setAttribute('label', label);
        el.setAttribute('route', item.route || item.link || '#');

        if (item.icon) {
            el.setAttribute('icon', item.icon);
        } else if (item.image) {
            el.setAttribute('image', item.image);
        }

        if (item.avatarText) { el.setAttribute('avatarText', item.avatarText); }
        if (item.id) { el.setAttribute('data-item-id', item.id); }
        const hasSubMenu = (item.subMenu?.length || item.subMenuItems?.length);
        if (hasSubMenu) { el.setAttribute('submenu', ''); }
        if (collapsed) { el.setAttribute('collapsed', 'true'); }
        if (item.isActive) { el.setAttribute('active', ''); }

        return el;
    }

    /**
     * Finds a menu item or slotted item by label name.
     * Returns the item data with its submenu if applicable.
     * @param {string} label - The label of the menu item to find
     * @returns {Object|null}
     */
    #findMenuItem(label) {
        if (this.menuData.topMenuItems || this.menuData.middleMenuItems || this.menuData.bottomMenuItems) {
            const allItems = [
                ...(this.menuData.topMenuItems || []),
                ...(this.menuData.middleMenuItems || []),
                ...(this.menuData.bottomMenuItems || [])
            ];
            const fromMenu = allItems.find(item => (item.label || item.name) === label);
            if (fromMenu) { return fromMenu; }
        } else {
            const flat = Array.isArray(this.menuData) ? this.menuData : [this.menuData];
            const fromMenu = flat.find(item => (item.label || item.name) === label);
            if (fromMenu) { return fromMenu; }
        }

    }

    /**
     * Patches the global history.pushState and history.replaceState methods to emit
     * 'neptune-location-change' events for route tracking. Only applies the patch once.
     */
    patchHistoryNavigation() {
        if (window.neptunePushStatePatchApplied) { return; }
        const origPush = history.pushState.bind(history);
        const origReplace = history.replaceState.bind(history);
        history.pushState = function (state, title, url) {
            origPush(state, title, url);
            window.dispatchEvent(new CustomEvent('neptune-location-change', { detail: { route: String(url) } }));
        };
        history.replaceState = function (state, title, url) {
            origReplace(state, title, url);
            window.dispatchEvent(new CustomEvent('neptune-location-change', { detail: { route: String(url) } }));
        };
        window.neptunePushStatePatchApplied = true;
    }

    /**
     * Emits a neptune-sidenav-toggle event with the provided state.
     * @param {string} state - The new sidenav state
     */
    #emitToggle(state) {
        this.dispatchEvent(new CustomEvent('neptune-sidenav-toggle', {
            bubbles: true, composed: true,
            detail: { state }
        }));
    }
}

if (!customElements.get('neptune-sidenav')) {
    customElements.define('neptune-sidenav', Sidenav);
}

export default Sidenav;